/**
 * ProjectRepoSection - Project-level form to save, validate and repair the
 * GitHub connection used for clone, push, and pull-request operations.
 *
 * The token is write-only: the server never returns the plaintext, just a
 * status flag (`none` | `valid` | `invalid`) and the last error if any. The
 * UI is hidden for users without the `p.project.set_repo_credentials`
 * permission.
 *
 * Server state is the default view. Local edits live in `draftUrl`/`override`
 * and are dropped once the save succeeds, so the panel goes back to mirroring
 * the projection rather than freezing whatever the last action returned. That
 * is what makes this a place to *fix* a broken connection: the stored URL, the
 * failure reason and the last successful validation are all on screen.
 */
import type {
  ProjectId,
  ProjectRepoCredentialStatus,
  SetProjectRepoCredentialsResult,
} from "@taskin-code/contracts";
import { useState } from "react";

import { useAuthStore, useHasProjectPermission } from "../../auth/authStore";
import { ensureNativeApi } from "../../nativeApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export interface ProjectRepoSectionProps {
  readonly projectId: ProjectId;
  readonly projectName?: string;
  readonly projectCwd?: string;
  readonly initialRepositoryUrl?: string | null;
  readonly initialStatus?: ProjectRepoCredentialStatus;
  readonly initialError?: string | null;
  readonly validatedAt?: string | null;
}

type FormState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "result"; result: SetProjectRepoCredentialsResult };

const STATUS_LABELS: Record<ProjectRepoCredentialStatus, string> = {
  none: "Sin conectar",
  valid: "Conectado",
  invalid: "Error",
};

const STATUS_BADGE: Record<ProjectRepoCredentialStatus, string> = {
  none: "bg-muted/50 text-muted-foreground",
  valid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  invalid: "bg-red-500/15 text-red-600 dark:text-red-300",
};

const STATUS_DOT: Record<ProjectRepoCredentialStatus, string> = {
  none: "bg-muted-foreground/40",
  valid: "bg-emerald-500",
  invalid: "bg-red-500",
};

function formatValidatedAt(iso: string): string | null {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
}

export function ProjectRepoSection({
  projectId,
  projectName,
  projectCwd,
  initialRepositoryUrl,
  initialStatus = "none",
  initialError = null,
  validatedAt = null,
}: ProjectRepoSectionProps) {
  // Two ways in, matching what `wsServer` will actually accept: the global
  // grant, which covers every project at once, or `p.edit` on this one. The
  // second is what makes Settings → Proyectos usable by whoever administers a
  // single repository without handing them the whole installation.
  const hasGlobalGrant = useAuthStore((state) =>
    state.globalPermissions.has("p.project.set_repo_credentials"),
  );
  const canEditThisProject = useHasProjectPermission(projectId, "p.edit");
  const canSetCredentials = hasGlobalGrant || canEditThisProject;

  // `null` means "follow the server". Anything else is an unsaved local edit.
  const [draftUrl, setDraftUrl] = useState<string | null>(null);
  const [override, setOverride] = useState<{
    readonly status: ProjectRepoCredentialStatus;
    readonly error: string | null;
  } | null>(null);
  const [accessToken, setAccessToken] = useState("");
  const [form, setForm] = useState<FormState>({ kind: "idle" });

  if (!canSetCredentials) {
    return null;
  }

  const repositoryUrl = draftUrl ?? initialRepositoryUrl ?? "";
  const status = override?.status ?? initialStatus;
  const lastError = override ? override.error : initialError;
  const submitting = form.kind === "submitting";
  const validatedLabel = validatedAt === null ? null : formatValidatedAt(validatedAt);
  const inputId = `repo-url-${projectId}`;
  const tokenInputId = `repo-token-${projectId}`;

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    if (repositoryUrl.trim() === "" || accessToken.trim() === "") {
      setOverride({ status: "invalid", error: "URL y token son requeridos" });
      setForm({ kind: "idle" });
      return;
    }
    setForm({ kind: "submitting" });
    try {
      const api = ensureNativeApi();
      const result = await api.orchestration.setProjectRepoCredentials({
        projectId,
        repositoryUrl: repositoryUrl.trim(),
        accessToken: accessToken.trim(),
      });
      setForm({ kind: "result", result });
      setOverride({ status: result.status, error: result.error ?? null });
      if (result.status === "valid") {
        setAccessToken("");
        // The URL is persisted now, so stop shadowing the projection.
        setDraftUrl(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falla al guardar credenciales";
      setForm({ kind: "result", result: { status: "invalid", error: message } });
      setOverride({ status: "invalid", error: message });
    }
  };

  const handleRevalidate = async () => {
    setForm({ kind: "submitting" });
    try {
      const api = ensureNativeApi();
      const result = await api.orchestration.validateProjectRepoCredentials({ projectId });
      setForm({ kind: "result", result });
      setOverride({ status: result.status, error: result.error ?? null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falla al validar";
      setForm({ kind: "result", result: { status: "invalid", error: message } });
      setOverride({ status: "invalid", error: message });
    }
  };

  const handleClear = async () => {
    setForm({ kind: "submitting" });
    try {
      const api = ensureNativeApi();
      await api.orchestration.clearProjectRepoCredentials({ projectId });
      setOverride({ status: "none", error: null });
      setDraftUrl("");
      setAccessToken("");
      setForm({ kind: "idle" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falla al limpiar";
      setForm({ kind: "result", result: { status: "invalid", error: message } });
      setOverride({ status: "invalid", error: message });
    }
  };

  return (
    <section
      className={`flex flex-col gap-4 rounded-xl border border-l-[3px] bg-card/40 p-5 transition-colors ${
        status === "invalid"
          ? "border-border/60 border-l-red-500/70"
          : "border-border/60 border-l-brand/70"
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {projectName ? (
            <h4 className="truncate text-[15px] font-semibold tracking-tight text-brand-foreground">
              {projectName}
            </h4>
          ) : null}
          <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/70">
            {projectCwd ?? "GitHub repository"}
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE[status]}`}
        >
          <span className={`size-1.5 rounded-full ${STATUS_DOT[status]}`} aria-hidden="true" />
          {STATUS_LABELS[status]}
        </span>
      </header>

      {status === "invalid" && lastError ? (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300">
          <span className="font-medium">No conecta:</span> {lastError}
        </div>
      ) : null}

      {status === "valid" && validatedLabel ? (
        <p className="text-[11px] text-muted-foreground/80">
          Última validación correcta: {validatedLabel}
        </p>
      ) : null}

      <form onSubmit={handleSave} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={inputId}>Repository URL</Label>
          <Input
            id={inputId}
            type="text"
            placeholder="https://github.com/midasred/mi-repo.git"
            value={repositoryUrl}
            disabled={submitting}
            onChange={(e) => setDraftUrl(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={tokenInputId}>Access token (fine-grained PAT)</Label>
          <Input
            id={tokenInputId}
            type="password"
            placeholder={status === "valid" ? "•••••••• guardado" : "github_pat_..."}
            value={accessToken}
            disabled={submitting}
            onChange={(e) => setAccessToken(e.target.value)}
            autoComplete="new-password"
            spellCheck={false}
          />
          <p className="text-[11px] text-muted-foreground">
            Permisos mínimos en el PAT: <code>Contents: read/write</code> y{" "}
            <code>Pull requests: write</code> sobre el repo.
            {status === "valid"
              ? " Déjalo vacío para conservar el token actual y usa Re-validar."
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Guardando…" : status === "none" ? "Conectar" : "Guardar y validar"}
          </Button>
          {status !== "none" ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={submitting}
                onClick={handleRevalidate}
              >
                Re-validar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={submitting}
                onClick={handleClear}
              >
                Desconectar
              </Button>
            </>
          ) : null}
        </div>
      </form>
    </section>
  );
}
